import { supabase } from '@/integrations/supabase/client';
import { ConsultoresSupabaseService } from './consultoresService';

export interface User {
  id: string; // UUID do Supabase
  email: string;
  username: string; // Nome de usuário único para login
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  tenant_id: string | null; // UUID do Supabase
  tenant?: {
    id: string; // UUID do Supabase
    nome: string;
  };
  created_at: string;
  updated_at: string;
  deleted_at?: string | null; // Data da exclusão lógica
}

export interface CreateUserDTO {
  email: string;
  username: string; // Nome de usuário único
  password: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  tenant_id: string | null;
  supervisorId?: number;
}

export interface UpdateUserDTO {
  email?: string;
  username?: string; // Nome de usuário único
  first_name?: string;
  last_name?: string;
  role?: string;
  status?: string;
  tenant_id?: string | null;
  supervisorId?: number;
}

export interface Tenant {
  id: string;
  nome: string;
  status: string;
  logo_url?: string | null;
  tipo_unidade?: 'MATRIZ' | 'ASSOCIADO' | 'REPRESENTACAO' | null;
  matriz_id?: number | null;
  created_at: string;
  updated_at: string;
  metas_vendas?: number;
  metas_contratos?: number;
  metas_faturamento?: number;
  _count?: {
    users: number;
    contratos: number;
  };
}

export interface CreateTenantData {
  nome: string;
  status: string;
  logo_url?: string | null;
  tipo_unidade?: 'ASSOCIADO' | 'REPRESENTACAO';
  matriz_id?: number | null;
  metas_vendas?: number;
  metas_contratos?: number;
  metas_faturamento?: number;
}

export interface UpdateTenantData {
  id: string;
  nome: string;
  ativo: boolean;
  logo_url?: string | null;
  metas_vendas?: number;
  metas_contratos?: number;
  metas_faturamento?: number;
}

// Interface interna do Supabase removida - não utilizada

const mapearUser = (user: any): User => {
  return {
    id: user.id, // UUID do Supabase
    email: user.email,
    username: user.username, // Nome de usuário único
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    status: user.status || 'ATIVO', // profiles não tem status, usar padrão
    tenant_id: user.tenant_id,
    tenant: user.tenants ? {
      id: user.tenants.id,
      nome: user.tenants.nome,
    } : undefined,
    created_at: user.created_at,
    updated_at: user.updated_at,
    deleted_at: user.deleted_at || null,
  };
};

const mapearTenant = (tenant: any): Tenant => {
  return {
    id: tenant.id,
    nome: tenant.nome,
    status: tenant.status,
    logo_url: tenant.logo_url,
    tipo_unidade: tenant.tipo_unidade ?? null,
    matriz_id: tenant.matriz_id ?? null,
    created_at: tenant.created_at,
    updated_at: tenant.updated_at,
    metas_vendas: tenant.metas_vendas,
    metas_contratos: tenant.metas_contratos,
    metas_faturamento: tenant.metas_faturamento,
  };
};

const mapearTenantWithCounts = (tenant: any): Tenant => {
  return {
    id: tenant.id,
    nome: tenant.nome,
    status: tenant.status,
    logo_url: tenant.logo_url,
    tipo_unidade: tenant.tipo_unidade ?? null,
    matriz_id: tenant.matriz_id ?? null,
    created_at: tenant.created_at,
    updated_at: tenant.updated_at,
    metas_vendas: tenant.metas_vendas,
    metas_contratos: tenant.metas_contratos,
    metas_faturamento: tenant.metas_faturamento,
    _count: {
      users: tenant._count?.users || 0,
      contratos: tenant._count?.contratos || 0,
    },
  };
};

export class SuperadminSupabaseService {
  /**
   * Lista todos os usuários (apenas os não deletados)
   */
  async getUsers(): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          tenants(*)
        `)
        .is('deleted_at', null) // Filtrar apenas usuários não deletados
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao listar usuários:', error);
        throw error;
      }

      return data?.map(mapearUser) || [];
    } catch (error) {
      console.error('Erro ao listar usuários:', error);
      throw error;
    }
  }

  /**
   * Cria um novo usuário (edge atômica: auth + profile + consultor + rollback)
   */
  async createUser(userData: CreateUserDTO): Promise<User> {
    try {
      console.log('🚀 Iniciando criação do usuário:', userData.email);

      let resolvedRole = userData.role;
      if (userData.tenant_id) {
        const { resolveRoleForTenantId } = await import(
          '@/features/superadmin/utils/tenantRoleResolver'
        );
        resolvedRole = await resolveRoleForTenantId(userData.tenant_id, userData.role);
      }

      const userDataResolved = { ...userData, role: resolvedRole };
      const rolesComSupervisorObrigatorio = ['RELACIONADOR', 'LIDER_VENDAS'];

      if (rolesComSupervisorObrigatorio.includes(userDataResolved.role) && !userDataResolved.supervisorId) {
        throw new Error('Supervisor é obrigatório para líder de vendas e relacionador.');
      }

      const { data: edgeData, error: edgeError } = await supabase.functions.invoke(
        'superadmin-convidar-usuario',
        {
          body: {
            email: userDataResolved.email,
            password: userDataResolved.password,
            first_name: userDataResolved.first_name,
            last_name: userDataResolved.last_name,
            username: userDataResolved.username,
            role: userDataResolved.role,
            status: userDataResolved.status || 'ACTIVE',
            tenant_id: userDataResolved.tenant_id ?? null,
            supervisorId: userDataResolved.supervisorId ?? null,
          },
        }
      );

      if (edgeError) {
        console.error('Erro na edge superadmin-convidar-usuario:', edgeError);
        const msg =
          (edgeData as any)?.error ||
          edgeError.message ||
          'Falha ao criar membro';
        throw new Error(msg);
      }

      if ((edgeData as any)?.error) {
        throw new Error(String((edgeData as any).error));
      }

      return mapearUser(edgeData);
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      throw error;
    }
  }

  /**
   * Atualiza um usuário existente
   */
  async updateUser(userId: string, userData: UpdateUserDTO): Promise<User> {
    try {
      console.log('🚀 Iniciando atualização do usuário:', userId);

      const updateData: any = {};

      if (userData.email !== undefined) updateData.email = userData.email;
      if (userData.username !== undefined) updateData.username = userData.username;
      if (userData.first_name !== undefined) updateData.first_name = userData.first_name;
      if (userData.last_name !== undefined) updateData.last_name = userData.last_name;
      if (userData.role !== undefined) {
        updateData.role = userData.role;
        updateData.is_superadmin = userData.role === 'SUPER_ADMIN';
      }
      if (userData.status !== undefined) updateData.status = userData.status;
      if (userData.tenant_id !== undefined) updateData.tenant_id = userData.tenant_id;

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select(`
          *,
          tenants(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar usuário:', error);
        throw error;
      }

      console.log('✅ Usuário atualizado com sucesso');

      // Se foi fornecido um supervisorId, atualizar ou criar o consultor
      if (userData.supervisorId !== undefined) {
        try {
          // Buscar o consultor associado ao usuário
          const { data: consultor, error: consultorError } = await supabase
            .from('consultores')
            .select('id')
            .eq('user_id', userId)
            .is('deleted_at', null)
            .maybeSingle();

          if (consultorError) {
            console.warn('Erro ao buscar consultor para atualização de supervisor:', consultorError);
          } else if (consultor) {
            // Atualizar o supervisor_id do consultor existente
            const { error: updateConsultorError } = await supabase
              .from('consultores')
              .update({ supervisor_id: userData.supervisorId })
              .eq('id', consultor.id);

            if (updateConsultorError) {
              console.error('Erro ao atualizar supervisor do consultor:', updateConsultorError);
            } else {
              console.log('✅ Supervisor do consultor atualizado com sucesso');
            }
          } else {
            // Se consultor não existe, tentar criar um novo baseado no profile atualizado
            console.log('Consultor não encontrado, tentando criar novo consultor...');
            const rolesQuePrecisamConsultor = ['ASSOCIADO', 'SUPERVISOR', 'RELACIONADOR', 'LIDER_VENDAS'];

            if (rolesQuePrecisamConsultor.includes(data.role)) {
              const res = await ConsultoresSupabaseService.criarConsultorDeProfile(
                data,
                userData.supervisorId
              );
              if (res.success) {
                console.log('✅ Novo consultor criado com sucesso durante a atualização');
              } else {
                console.warn('⚠️ Falha ao criar novo consultor:', res.message);
              }
            } else {
              console.log(`Role ${data.role} não requer registro na tabela de consultores.`);
            }
          }
        } catch (error) {
          console.error('Erro no fluxo de atualização/criação de consultor:', error);
        }
      }

      return mapearUser(data);
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw error;
    }
  }

  /**
   * Verifica vinculações de um usuário antes da exclusão
   */
  async checkUserDependencies(userId: string): Promise<{
    hasClientes: boolean;
    hasContratos: boolean;
    clientesCount: number;
    contratosCount: number;
    totalDependencies: number;
  }> {
    try {
      // Verificar clientes vinculados
      const { count: clientesCount } = await supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId);

      // Verificar contratos vinculados
      const { count: contratosCount } = await supabase
        .from('contratos')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId);

      const clientes = clientesCount || 0;
      const contratos = contratosCount || 0;
      const total = clientes + contratos;

      return {
        hasClientes: clientes > 0,
        hasContratos: contratos > 0,
        clientesCount: clientes,
        contratosCount: contratos,
        totalDependencies: total,
      };
    } catch (error) {
      console.error('Erro ao verificar dependências do usuário:', error);
      throw error;
    }
  }

  /**
   * Desvincula dados de um usuário antes da exclusão
   */
  async unlinkUserData(userId: string): Promise<void> {
    try {
      console.log('🚀 Iniciando desvinculação de dados do usuário:', userId);

      // Desvincular clientes (setar created_by para null)
      const { error: clientesError } = await supabase
        .from('clientes')
        .update({ created_by: null } as any)
        .eq('created_by', userId);

      if (clientesError) {
        console.error('Erro ao desvincular clientes:', clientesError);
        throw clientesError;
      }

      // Desvincular contratos (setar created_by para null)
      const { error: contratosError } = await supabase
        .from('contratos')
        .update({ created_by: null } as any)
        .eq('created_by', userId);

      if (contratosError) {
        console.error('Erro ao desvincular contratos:', contratosError);
        throw contratosError;
      }

      console.log('✅ Dados desvinculados com sucesso');
    } catch (error) {
      console.error('❌ Erro ao desvincular dados do usuário:', error);
      throw error;
    }
  }

  /**
   * Redefine a senha de um usuário (apenas para superadmin)
   */
  async resetPassword(userId: string, newPassword: string, permanent: boolean = false): Promise<void> {
    try {
      console.log(`🚀 Iniciando ${permanent ? 'alteração permanente' : 'redefinição'} de senha do usuário:`, userId);

      const { data, error } = await supabase.rpc('admin_reset_user_password', {
        p_user_id: userId,
        p_new_password: newPassword,
        p_permanent: permanent
      });

      if (error) {
        console.error('Erro ao redefinir senha via RPC:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.message || 'Falha ao redefinir senha');
      }

      console.log(`✅ Senha ${permanent ? 'alterada permanentemente' : 'redefinida'} com sucesso`);
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      throw error;
    }
  }

  /**
   * Exclui um usuário (exclusão lógica)
   */
  async deleteUser(userId: string, forceUnlink: boolean = false): Promise<void> {
    try {
      console.log('🚀 Iniciando exclusão lógica do usuário:', userId);

      // Verificar se o usuário existe e não está já inativo
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id, deleted_at, status')
        .eq('id', userId)
        .single();

      if (checkError) {
        console.error('Erro ao verificar usuário:', checkError);
        throw new Error('Usuário não encontrado');
      }

      // Verificar se já foi deletado (tem deleted_at preenchido)
      if (existingUser.deleted_at) {
        throw new Error('Usuário já foi desativado anteriormente');
      }

      // Se forceUnlink for true, desvincular dados antes da exclusão
      if (forceUnlink) {
        await this.unlinkUserData(userId);
      }

      const currentTimestamp = new Date().toISOString();

      // Marcar usuário como INACTIVE e registrar data de exclusão
      const { error } = await supabase
        .from('profiles')
        .update({
          status: 'INACTIVE',
          deleted_at: currentTimestamp,
          updated_at: currentTimestamp
        })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao desativar usuário:', error);
        throw error;
      }

      // Atualizar status do consultor correspondente para 'inativo'
      const { error: consultorError } = await supabase
        .from('consultores')
        .update({
          status: 'inativo',
          updated_at: currentTimestamp
        })
        .eq('user_id', userId);

      if (consultorError) {
        console.warn('Aviso: Usuário desativado mas falha ao atualizar consultor:', consultorError.message);
        // Não falha a operação principal se não conseguir atualizar o consultor
      }

      console.log('✅ Usuário marcado como INACTIVE e desativado com sucesso em:', currentTimestamp);
    } catch (error) {
      console.error('❌ Erro ao deletar usuário:', error);
      throw error;
    }
  }

  /**
   * Busca estatísticas do dashboard
   */
  async getDashboardStats(): Promise<{ users: number; tenants: number; clientes: number; contratos: number }> {
    try {
      // Contar usuários (apenas os não deletados)
      const { count: usersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .is('deleted_at', null);

      // Contar tenants ativos
      const { count: tenantsCount } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'ATIVO');

      // Contar clientes
      const { count: clientesCount } = await supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true });

      // Contar contratos
      const { count: contratosCount } = await supabase
        .from('contratos')
        .select('*', { count: 'exact', head: true });

      return {
        users: usersCount || 0,
        tenants: tenantsCount || 0,
        clientes: clientesCount || 0,
        contratos: contratosCount || 0,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  }
}

export class TenantSupabaseService {
  /**
   * Lista todos os tenants
   */
  async findAll(): Promise<Tenant[]> {
    return this.fetchTenants();
  }

  /**
   * Lista todos os tenants
   */
  async fetchTenants(): Promise<Tenant[]> {
    try {
      // Super Admin precisa ver ATIVO e INATIVO (reativar, auditar associados legado)
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('nome', { ascending: true });

      if (tenantsError) {
        console.error('Erro ao listar tenants:', tenantsError);
        throw tenantsError;
      }

      if (!tenants || tenants.length === 0) {
        return [];
      }

      // Buscar contagens para cada tenant
      const tenantsWithCounts = await Promise.all(
        tenants.map(async (tenant) => {
          // Contar usuários (profiles) para este tenant (apenas não deletados)
          const { count: usersCount, error: usersError } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenant.id)
            .is('deleted_at', null);

          if (usersError) {
            console.error(`Erro ao contar usuários para tenant ${tenant.id}:`, usersError);
          }

          // Contar contratos para este tenant
          const { count: contratosCount, error: contratosError } = await supabase
            .from('contratos')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenant.id);

          if (contratosError) {
            console.error(`Erro ao contar contratos para tenant ${tenant.id}:`, contratosError);
          }

          return {
            ...tenant,
            _count: {
              users: usersCount || 0,
              contratos: contratosCount || 0,
            },
          };
        })
      );

      return tenantsWithCounts
        .map(mapearTenantWithCounts)
        .sort((a, b) => {
          // Ativas primeiro, depois nome
          const sa = a.status === 'ATIVO' ? 0 : 1;
          const sb = b.status === 'ATIVO' ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
        });
    } catch (error) {
      console.error('Erro ao listar tenants:', error);
      throw error;
    }
  }

  /**
   * Busca um tenant específico
   */
  async fetchTenant(id: string): Promise<Tenant> {
    try {
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', id)
        .single();

      if (tenantError) {
        console.error('Erro ao buscar tenant:', tenantError);
        throw tenantError;
      }

      // Buscar contagens para este tenant (apenas usuários não deletados)
      const { count: usersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id)
        .is('deleted_at', null);

      if (usersError) {
        console.error(`Erro ao contar usuários para tenant ${tenant.id}:`, usersError);
      }

      const { count: contratosCount, error: contratosError } = await supabase
        .from('contratos')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id);

      if (contratosError) {
        console.error(`Erro ao contar contratos para tenant ${tenant.id}:`, contratosError);
      }

      const tenantWithCounts = {
        ...tenant,
        _count: {
          users: usersCount || 0,
          contratos: contratosCount || 0,
        },
      };

      return mapearTenantWithCounts(tenantWithCounts);
    } catch (error) {
      console.error('Erro ao buscar tenant:', error);
      throw error;
    }
  }

  /**
   * Cria um novo tenant
   */
  async createTenant(tenantData: CreateTenantData): Promise<Tenant> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .insert({
          nome: tenantData.nome,
          status: tenantData.status,
          logo_url: tenantData.logo_url,
          tipo_unidade: tenantData.tipo_unidade ?? 'ASSOCIADO',
          matriz_id: tenantData.matriz_id ?? 1,
          metas_vendas: tenantData.metas_vendas,
          metas_contratos: tenantData.metas_contratos,
          metas_faturamento: tenantData.metas_faturamento,
        } as any)
        .select('*')
        .single();

      if (error) {
        console.error('Erro ao criar tenant:', error);
        throw error;
      }

      return mapearTenant(data);
    } catch (error) {
      console.error('Erro ao criar tenant:', error);
      throw error;
    }
  }

  /**
   * Atualiza um tenant existente
   */
  async updateTenant(tenantData: UpdateTenantData): Promise<Tenant> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .update({
          nome: tenantData.nome,
          status: tenantData.ativo ? 'ATIVO' : 'INATIVO',
          logo_url: tenantData.logo_url !== undefined ? tenantData.logo_url : undefined,
          metas_vendas: tenantData.metas_vendas,
          metas_contratos: tenantData.metas_contratos,
          metas_faturamento: tenantData.metas_faturamento,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', tenantData.id)
        .select('*')
        .single();

      if (error) {
        console.error('Erro ao atualizar tenant:', error);
        throw error;
      }

      return mapearTenant(data);
    } catch (error) {
      console.error('Erro ao atualizar tenant:', error);
      throw error;
    }
  }

  /**
   * Exclui um tenant
   */
  async deleteTenant(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('tenants')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir tenant:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro ao excluir tenant:', error);
      throw error;
    }
  }

  /**
   * Ativa/desativa um tenant
   */
  async toggleTenantStatus(id: string, active: boolean): Promise<Tenant> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .update({
          status: active ? 'ATIVO' : 'INATIVO',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single();

      if (error) {
        console.error('Erro ao alterar status do tenant:', error);
        throw error;
      }

      return mapearTenant(data);
    } catch (error) {
      console.error('Erro ao alterar status do tenant:', error);
      throw error;
    }
  }
}
